/**
 * Garson POS — Rezervasyon listesi (bugün + yarın).
 *
 * Bugün gelecek rezervasyonları zamana göre listele, "Misafirleri Geçir"
 * (seat) action ile masa açılır.
 */
import { Stack, Card, Group, Text, Badge, Button, Loader, Center, Divider } from "@mantine/core";
import { IconCalendar, IconCheck, IconUserPlus } from "@tabler/icons-react";
import { notifications } from "@mantine/notifications";
import { useResource, useAction } from "../../../shared/api";
import { PageHeader, EmptyState, MoneyDisplay } from "../../../shared/ui";

const STATUS_COLOR: Record<string, string> = {
  AWAITING_PAYMENT: "yellow",
  CONFIRMED: "green",
  SEATED: "blue",
  COMPLETED: "gray",
  NO_SHOW: "dark",
  CANCELLED: "red",
};

const STATUS_LABEL: Record<string, string> = {
  AWAITING_PAYMENT: "Kapora Bekleniyor",
  CONFIRMED: "Onaylı",
  SEATED: "Masa Açıldı",
  COMPLETED: "Tamamlandı",
  NO_SHOW: "Gelmedi",
  CANCELLED: "İptal",
};

export function POSReservationsPage() {
  const { data, isLoading } = useResource<any>("reservations");
  const seat = useAction("reservations", "seat");
  const list: any[] = data?.results || data || [];

  if (isLoading) return <Center h={300}><Loader /></Center>;

  // Bugün ve yarın'a filtrele
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const dayAfter = new Date(today);
  dayAfter.setDate(today.getDate() + 2);

  const upcoming = list
    .filter((r: any) => {
      const t = new Date(r.starts_at);
      return t >= today && t < dayAfter && !["COMPLETED", "CANCELLED", "NO_SHOW"].includes(r.status);
    })
    .sort((a: any, b: any) => new Date(a.starts_at).getTime() - new Date(b.starts_at).getTime());

  const todayReservations = upcoming.filter((r: any) => new Date(r.starts_at).toDateString() === today.toDateString());
  const tomorrowReservations = upcoming.filter((r: any) => new Date(r.starts_at).toDateString() !== today.toDateString());

  async function doSeat(id: string) {
    try {
      await seat.mutateAsync({ id });
      notifications.show({ message: "Misafirler masaya geçirildi", color: "green" });
    } catch (e: any) {
      notifications.show({ message: e?.response?.data?.detail || "Hata", color: "red" });
    }
  }

  if (upcoming.length === 0) {
    return (
      <Stack gap="md">
        <PageHeader title="Rezervasyonlar" subtitle="Bugün ve yarın için bekleyen rezervasyon yok" />
        <EmptyState title="Rezervasyon yok" icon={<IconCalendar size={48} />} />
      </Stack>
    );
  }

  return (
    <Stack gap="md">
      <PageHeader title="Rezervasyonlar" subtitle={`${upcoming.length} bekleyen`} />

      {todayReservations.length > 0 && (
        <>
          <Group>
            <Badge size="lg" color="blue">Bugün ({todayReservations.length})</Badge>
          </Group>
          {todayReservations.map((r: any) => <ReservationCard key={r.id} r={r} onSeat={doSeat} />)}
        </>
      )}

      {tomorrowReservations.length > 0 && (
        <>
          <Divider my="md" />
          <Group>
            <Badge size="lg" color="gray">Yarın ({tomorrowReservations.length})</Badge>
          </Group>
          {tomorrowReservations.map((r: any) => <ReservationCard key={r.id} r={r} onSeat={doSeat} />)}
        </>
      )}
    </Stack>
  );
}

function ReservationCard({ r, onSeat }: { r: any; onSeat: (id: string) => void }) {
  const time = new Date(r.starts_at).toLocaleTimeString("tr-TR", { hour: "2-digit", minute: "2-digit" });
  const canSeat = r.status === "CONFIRMED";
  return (
    <Card padding="md" radius="md" withBorder>
      <Stack gap="xs">
        <Group justify="space-between">
          <Group>
            <Text fw={700} size="xl">{time}</Text>
            <Badge color={STATUS_COLOR[r.status]} size="lg">{STATUS_LABEL[r.status] || r.status}</Badge>
          </Group>
          <Badge size="lg" variant="light" leftSection={<IconUserPlus size={14} />}>
            {r.party_size} kişi
          </Badge>
        </Group>

        <Group justify="space-between">
          <Text fw={500}>
            {r.customer_name || r.customer || "İsimsiz"}
          </Text>
          {r.customer_phone && <Text size="sm" c="dimmed">{r.customer_phone}</Text>}
        </Group>

        {r.table && <Text size="sm" c="dimmed">Masa: {r.table_name || r.table}</Text>}
        {r.notes && <Text size="sm" fs="italic">{r.notes}</Text>}

        {r.deposit_minor > 0 && (
          <Group gap="xs">
            <Text size="sm">Kapora:</Text>
            <MoneyDisplay minor={r.deposit_paid_minor || 0} fw={600} />
            <Text size="sm" c="dimmed">/ <MoneyDisplay minor={r.deposit_minor} /></Text>
          </Group>
        )}

        {canSeat && (
          <Button leftSection={<IconCheck size={16} />} onClick={() => onSeat(r.id)} mt="xs" size="md">
            Misafirleri Masaya Geçir
          </Button>
        )}
      </Stack>
    </Card>
  );
}
